// =====================================================================
// VueArbre — l'arbre dessiné
// =====================================================================
// SVG plutôt que canvas : le texte reste sélectionnable et lisible par un
// lecteur d'écran, et la famille tient largement dans le nombre de nœuds
// que le DOM encaisse sans effort.
//
// Déplacement et zoom gérés aux pointer events, donc souris et doigts
// avec le même code, pincement à deux doigts compris.
// =====================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import type { Lien, Parent } from '../../partage/types';
import {
  disposer,
  DIAMETRE_AVATAR,
  HAUTEUR_NOEUD,
  LARGEUR_NOEUD,
  type NoeudArbre,
} from '../../partage/disposition';
import { nomComplet } from './base';
import { FormesAvatar, trancheDepuisGeneration } from './Avatar';

interface Moi {
  personne_id: string;
  nom: string | null;
  prenoms: string | null;
  sexe: 'M' | 'F' | null;
}

const ZOOM_MIN = 0.35;
const ZOOM_MAX = 2.4;
const MARGE = 28;

export function VueArbre({
  moi,
  parents,
  liens,
  onChoisir,
}: {
  moi: Moi;
  parents: Parent[];
  liens: Lien[];
  onChoisir: (personneId: string) => void;
}) {
  const plan = useMemo(() => disposer(moi, parents, liens), [moi, parents, liens]);

  const conteneur = useRef<HTMLDivElement>(null);
  const [vue, setVue] = useState({ x: 0, y: 0, k: 1 });
  const [cadre, setCadre] = useState({ l: 320, h: 420 });
  const pointeurs = useRef(new Map<number, { x: number; y: number }>());
  const ecartInitial = useRef<{ d: number; k: number } | null>(null);
  const deplace = useRef(false);

  // Taille réelle du cadre, pour cadrer le contenu dessus.
  useEffect(() => {
    const el = conteneur.current;
    if (!el) return;
    const obs = new ResizeObserver(([e]) => {
      setCadre({ l: e.contentRect.width, h: e.contentRect.height });
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Au premier rendu, on cadre sur « moi » : c'est là que le regard part.
  useEffect(() => {
    const centre = plan.noeuds.find((n) => n.moi);
    if (!centre || cadre.l === 0) return;

    const k = Math.min(
      1,
      (cadre.l - MARGE * 2) / Math.max(plan.largeur, 1),
      (cadre.h - MARGE * 2) / Math.max(plan.hauteur, 1),
    );
    const echelle = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, k));

    // Si toute la famille tient dans le cadre, on la centre en entier :
    // sinon le haut de l'arbre se retrouve coupé dès l'ouverture. Si elle
    // déborde, on centre sur « moi », qui est le point de départ du regard.
    const tient =
      plan.largeur * echelle <= cadre.l - MARGE * 2 &&
      plan.hauteur * echelle <= cadre.h - MARGE * 2;

    const cibleX = tient ? plan.largeur / 2 : centre.x + LARGEUR_NOEUD / 2;
    const cibleY = tient ? plan.hauteur / 2 : centre.y + HAUTEUR_NOEUD / 2;

    setVue({
      k: echelle,
      x: cadre.l / 2 - cibleX * echelle,
      y: cadre.h / 2 - cibleY * echelle,
    });
    // Recadrer seulement quand la famille ou la taille du cadre change.
  }, [plan, cadre.l, cadre.h]);

  // --- Déplacement et pincement --------------------------------------

  function onPointerDown(e: React.PointerEvent) {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pointeurs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    deplace.current = false;
  }

  function onPointerMove(e: React.PointerEvent) {
    const actifs = pointeurs.current;
    if (!actifs.has(e.pointerId)) return;

    const precedent = actifs.get(e.pointerId)!;
    actifs.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (actifs.size === 1) {
      const dx = e.clientX - precedent.x;
      const dy = e.clientY - precedent.y;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) deplace.current = true;
      setVue((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
      return;
    }

    if (actifs.size === 2) {
      deplace.current = true;
      const [a, b] = [...actifs.values()];
      const distance = Math.hypot(a.x - b.x, a.y - b.y);

      if (!ecartInitial.current) {
        ecartInitial.current = { d: distance, k: vue.k };
        return;
      }

      const rapport = distance / ecartInitial.current.d;
      const k = borner(ecartInitial.current.k * rapport);
      const rect = conteneur.current?.getBoundingClientRect();
      const cx = (a.x + b.x) / 2 - (rect?.left ?? 0);
      const cy = (a.y + b.y) / 2 - (rect?.top ?? 0);
      zoomerVers(cx, cy, k);
    }
  }

  function onPointerUp(e: React.PointerEvent) {
    pointeurs.current.delete(e.pointerId);
    if (pointeurs.current.size < 2) ecartInitial.current = null;
  }

  function onWheel(e: React.WheelEvent) {
    const rect = conteneur.current?.getBoundingClientRect();
    const cx = e.clientX - (rect?.left ?? 0);
    const cy = e.clientY - (rect?.top ?? 0);
    zoomerVers(cx, cy, borner(vue.k * (e.deltaY < 0 ? 1.12 : 1 / 1.12)));
  }

  /** Zoome en gardant fixe le point (cx, cy) de l'écran. */
  function zoomerVers(cx: number, cy: number, k: number) {
    setVue((v) => ({
      k,
      x: cx - ((cx - v.x) / v.k) * k,
      y: cy - ((cy - v.y) / v.k) * k,
    }));
  }

  function borner(k: number) {
    return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, k));
  }

  function recadrer() {
    setCadre((c) => ({ ...c }));
    const centre = plan.noeuds.find((n) => n.moi);
    if (!centre) return;
    const k = borner(
      Math.min(
        1,
        (cadre.l - MARGE * 2) / Math.max(plan.largeur, 1),
        (cadre.h - MARGE * 2) / Math.max(plan.hauteur, 1),
      ),
    );
    const tient =
      plan.largeur * k <= cadre.l - MARGE * 2 &&
      plan.hauteur * k <= cadre.h - MARGE * 2;

    setVue({
      k,
      x: cadre.l / 2 - (tient ? plan.largeur / 2 : centre.x + LARGEUR_NOEUD / 2) * k,
      y: cadre.h / 2 - (tient ? plan.hauteur / 2 : centre.y + HAUTEUR_NOEUD / 2) * k,
    });
  }

  return (
    <div className="arbre-cadre">
      <div
        ref={conteneur}
        className="arbre-toile"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={onWheel}
      >
        <svg
          width="100%"
          height="100%"
          role="img"
          aria-label="Arbre généalogique"
        >
          <g transform={`translate(${vue.x} ${vue.y}) scale(${vue.k})`}>
            {/* Barres de couple : les deux parents d'un même enfant sont
                reliés, et une seule ligne descend de leur milieu. */}
            {plan.barres.map((b, i) => (
              <path
                key={`b${i}`}
                className="arbre-couple"
                d={`M ${b.x1} ${b.y} H ${b.x2}`}
              />
            ))}
            {plan.aretes.map((a, i) => (
              <path
                key={`a${i}`}
                className="arbre-trait"
                d={trait(a.depart, a.basParent, a.enfant)}
              />
            ))}
            {plan.noeuds.map((n) => (
              <Noeud
                key={n.id}
                noeud={n}
                onChoisir={() => {
                  if (!deplace.current) onChoisir(n.id);
                }}
              />
            ))}
          </g>
        </svg>
      </div>

      <div className="arbre-commandes">
        <button
          className="bouton secondaire mince"
          onClick={() => zoomerVers(cadre.l / 2, cadre.h / 2, borner(vue.k / 1.25))}
          aria-label="Dézoomer"
        >
          −
        </button>
        <button className="bouton secondaire mince" onClick={recadrer}>
          Me recentrer
        </button>
        <button
          className="bouton secondaire mince"
          onClick={() => zoomerVers(cadre.l / 2, cadre.h / 2, borner(vue.k * 1.25))}
          aria-label="Zoomer"
        >
          +
        </button>
      </div>
    </div>
  );
}

/**
 * Trait orthogonal vers l'enfant : plus lisible qu'une diagonale.
 * Le segment horizontal se place sous le bas des blocs parents, pour ne
 * jamais traverser un nom — même quand le trait part de la barre de
 * couple, qui est à hauteur des portraits.
 */
function trait(
  depart: { x: number; y: number },
  basParent: number,
  enfant: NoeudArbre,
): string {
  const x2 = enfant.x + LARGEUR_NOEUD / 2;
  const y2 = enfant.y;
  const my = basParent + (y2 - basParent) / 2;
  return `M ${depart.x} ${depart.y} V ${my} H ${x2} V ${y2}`;
}

function Noeud({
  noeud,
  onChoisir,
}: {
  noeud: NoeudArbre;
  onChoisir: () => void;
}) {
  const sansNom = !noeud.nom && !noeud.prenoms;
  const nom = sansNom ? 'Nom inconnu' : nomComplet(noeud.nom, noeud.prenoms);
  const r = DIAMETRE_AVATAR / 2;
  const cx = LARGEUR_NOEUD / 2;

  return (
    <g
      className={`arbre-noeud${noeud.moi ? ' moi' : ''}${
        sansNom ? ' sans-nom' : ''
      }${noeud.enAttente ? ' en-attente' : ''}`}
      transform={`translate(${noeud.x} ${noeud.y})`}
      onClick={onChoisir}
      role="button"
      tabIndex={0}
      aria-label={`${nom}, ${noeud.libelle}${
        noeud.enAttente ? ", en attente d'envoi" : ''
      }`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onChoisir();
        }
      }}
    >
      {/* Zone de clic : tout le nœud, pas seulement le portrait. */}
      <rect
        width={LARGEUR_NOEUD}
        height={HAUTEUR_NOEUD}
        rx="10"
        className="arbre-zone"
      />

      {/* Portrait */}
      <g transform={`translate(${cx - r} 0) scale(${DIAMETRE_AVATAR / 100})`}>
        <FormesAvatar
          id={noeud.id}
          sexe={noeud.sexe}
          tranche={trancheDepuisGeneration(noeud.generation)}
          inconnu={sansNom}
        />
      </g>
      <circle cx={cx} cy={r} r={r - 0.9} className="arbre-anneau" />

      {/* Nom et relation */}
      <text x={cx} y={DIAMETRE_AVATAR + 18} className="arbre-nom">
        {tronquer(nom, 16)}
      </text>
      <text x={cx} y={DIAMETRE_AVATAR + 32} className="arbre-relation">
        {tronquer(noeud.libelle, 18)}
      </text>

      {noeud.decede && (
        <text x={cx + 26} y={DIAMETRE_AVATAR + 18} className="arbre-decede">
          †
        </text>
      )}
    </g>
  );
}

function tronquer(texte: string, max: number): string {
  return texte.length > max ? `${texte.slice(0, max - 1)}…` : texte;
}
